<template>
  <div :key="slideKey" :class="containerClass" class="container px-0">
    <div
      class="testimonial-content d-flex row g-0 flex-wrap py-space-sm py-md-space-xl px-space-sm px-md-space-lg">
      <div
        :class="imageContainerClass"
        class="d-flex justify-content-center px-space-md px-md-space-xxs pt-space-sm pb-space-md py-md-0 col-12 col-md-6 col-lg-4">
        <img
          :show="true"
          :data-src="slide.src"
          :alt="slide.imageAlt ? slide.imageAlt : slide.title"
          class="testimonial-image align-self-center px-space-xxs px-md-0 py-0 py-md-space-xs cta-lazy"
          :class="imageClass" />
      </div>
      <div
        :class="textContainerClass"
        class="testimonial-text align-self-center px-space-xxs pb-space-sm pb-md-0 col-12 col-md-6 col-lg-8">
        <h3 class="mb-space-xs" :class="titleClass">{{ slide.title }}</h3>
        <div class="mb-space-sm">
          <h4
            class="mb-0 testimonial-label"
            :class="labelClass"
            v-if="slide.labelA">
            {{ slide.labelA }}
            <br v-if="slide.labelB" />
            {{ slide.labelB }}
          </h4>
        </div>
        <div class="title-underline mb-space-sm"></div>
        <div :class="textClass">
          <div class="mb-space-sm" v-html="slide.bodyText"></div>
          <component
            v-if="displayCta"
            :is="ctaOpenModal ? 'button' : 'a'"
            class="modal-link d-inline-block"
            :class="ctaClass"
            :href="ctaOpenModal ? null : slide.link"
            :target="ctaOpenModal ? null : ctaLinkTarget"
            role="button"
            @click="ctaClick"
            @click.middle="ctaClick"
            :aria-label="slide.ctaText">
            {{ slide.ctaText }}
          </component>
        </div>
      </div>
    </div>
    <rds-modal
      :visible="modalShow"
      @hidden="handleModalClose($event)"
      :id="slideId.toString()"
      centered
      :border-radius="1">
      <template #header
        ><h5>{{ slide.title }}</h5></template
      >
      <div class="p-space-xs">
        <div class="pb-space-sm mb-0 modal-text" v-html="slide.modalText"></div>
        <div v-if="props.slide.videoId" class="ratio ratio-16x9">
          <iframe
            title="Video"
            class="embed-responsive-item"
            :src="videoEmbedUrl"></iframe>
        </div>
      </div>
    </rds-modal>
  </div>
</template>

<script setup lang="ts">
import { RdsModal } from "@rds-vue-ui/rds-modal";
import lozad from "lozad";
import { computed, onMounted, ref } from "vue";

export interface Slide {
  src: string;
  link?: string;
  imageAlt?: string;
  title: string;
  ctaText?: string;
  labelA?: string;
  labelB?: string;
  bodyText: string;
  modalText?: string;
  videoId?: string;
}

interface Props {
  slide: Slide;
  slideIndex?: number;
  roundImage?: boolean;
  alignImageRight?: boolean;
  backgroundVariant?: string;
  displayBorder?: boolean;
  borderVariant?: string;
  titleVariant?: string;
  titleSize?: string;
  labelVariant?: string;
  labelSize?: string;
  textVariant?: string;
  textSize?: string;
  displayCta?: boolean;
  ctaFontWeight?: string;
  ctaVariant?: string;
  ctaAsButton?: boolean;
  ctaButtonVariant?: string;
  ctaOpenModal?: boolean;
  ctaLinkTarget?: string;
}

const props = withDefaults(defineProps<Props>(), {
  slideIndex: 0,
  roundImage: true,
  alignImageRight: false,
  backgroundVariant: "dark-3",
  displayBorder: false,
  borderVariant: "light-2",
  titleVariant: "secondary",
  titleSize: "xl",
  labelVariant: "white",
  labelSize: "medium",
  textVariant: "white",
  textSize: "medium",
  displayCta: true,
  ctaFontWeight: "normal",
  ctaVariant: "secondary",
  ctaAsButton: false,
  ctaButtonVariant: "secondary",
  ctaOpenModal: true,
  ctaLinkTarget: "_self",
});

// events
const emits = defineEmits<{
  /**
   *  Emitted when the cta is clicked.
   */
  (e: "ctaClick", event: Event): void;
  /**
   *  Emitted when the modal is closed.
   */
  (e: "modalClose", event: Event): void;
}>();

// gen random card id, used for sliders and vue transitions
const slideId = Date.now();
const keyPrefix = Math.floor(Math.random() * 10000) + "ct";

const modalShow = ref(false);

const slideKey = computed(() => {
  return keyPrefix + props.slideIndex;
});

const videoEmbedUrl = computed(() => {
  return `https://www.youtube.com/embed/${props.slide.videoId}`;
});

const containerClass = computed(() => {
  return props.displayBorder
    ? `border border-${props.borderVariant} bg-${props.backgroundVariant}`
    : `bg-${props.backgroundVariant}`;
});

const imageClass = computed(() => {
  return props.roundImage ? ` rounded-circle ` : null;
});

const imageContainerClass = computed(() => {
  return props.alignImageRight ? ` testimonial-image-right ` : null;
});

const titleClass = computed(() => {
  return `text-${props.titleVariant} fs-${props.titleSize}`;
});

const labelClass = computed(() => {
  return `text-${props.labelVariant} fs-${props.labelSize}`;
});

const textContainerClass = computed(() => {
  return props.alignImageRight
    ? `pe-md-space-sm pe-lg-space-lg ps-lg-space-md`
    : `ps-md-space-sm ps-lg-space-lg pe-lg-space-md`;
});

const textClass = computed(() => {
  return `text-${props.textVariant} fs-${props.textSize}`;
});

const ctaClass = computed(() => {
  let classNames = "";
  classNames += `text-${props.ctaVariant} fw-${props.ctaFontWeight} `;
  if (props.ctaAsButton) {
    classNames += ` btn btn-${props.ctaButtonVariant}`;
  } else {
    classNames += ` btn rds-link btn-link text-decoration-underline p-0 m-0`;
  }
  return classNames;
});

const handleCtaClick = (event: Event) => {
  /*
    event will be PointerEvent Object or MouseEvent Object
    example: PointerEvent {isTrusted: true, PointerId: 1, width: 1, ...}
  */
  emits("ctaClick", event);
};

const handleModalClose = (event: Event) => {
  modalShow.value = false;
  emits("modalClose", event);
};

const ctaClick = (event: Event) => {
  handleCtaClick(event);
  if (props.ctaOpenModal) {
    modalShow.value = !modalShow.value;
  }
};

onMounted(() => {
  if (typeof window !== "undefined" && window !== null) {
    const observer = lozad(".cta-lazy");
    observer.observe();
  }
});
</script>

<style lang="scss" scoped>
.testimonial-content {
  align-content: center;
  min-height: 530px;
}
.modal-link {
  cursor: pointer;
}
.testimonial-image {
  max-width: 100%;
  min-height: 100px;
}
.testimonial-image-right {
  order: 1;
}
.title-underline {
  width: 5rem;
  height: 0.25rem;
  background-color: var(--rds-secondary, #ffc627);
}

.bg-dark-3 * a:focus {
  outline: 2px solid var(--rds-white, #ffffff);
}

@media screen and (max-width: 992px) {
  .testimonial-content {
    min-height: 600px;
  }
}

@media screen and (max-width: 768px) {
  .testimonial-content {
    min-height: 740px;
    align-content: flex-start;
  }
  .testimonial-text {
    text-align: center;
  }
  .title-underline {
    margin: auto;
  }
  .testimonial-label {
    line-height: 1.5;
  }
  .testimonial-image-right {
    order: 0;
  }
}

@media screen and (max-width: 576px) {
  .testimonial-content {
    min-height: 625px;
  }
}
</style>
